import { useState, useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import img1 from "../../assets/bannerImg1.jpeg";
import img2 from "../../assets/bannerImg2.jpeg";
import img3 from "../../assets/bannerImg.jpg";

const slides = [
  {
    id: 0,
    bg: img1,
    label: "Original Works",
    title: "Where Colour Becomes Emotion",
    paragraph:
      "Each canvas is a handcrafted original — layered with texture, light, and feeling. Own a piece that speaks before you find the words.",
    cta: "Explore the Collection",
  },
  {
    id: 1,
    bg: img2,
    label: "Commission a Piece",
    title: "A Painting Made for You, Alone",
    paragraph:
      "Tell me your vision — a memory, a place, a feeling — and I'll translate it into oil and pigment. Every commission is a collaboration.",
    cta: "Request a Commission",
  },
  {
    id: 2,
    bg: img3,
    label: "The Studio",
    title: "Painted by Hand. Signed with Intent.",
    paragraph:
      "From sketch to final varnish, every work leaves the studio only when it's truly finished. Quality you can see — and feel — from across the room.",
    cta: "Meet the Artist",
  },
];

/* ── Keyframes ── */
const grain = keyframes`
  0%, 100% { transform: translate(0, 0); }
  10%       { transform: translate(-2%, -3%); }
  30%       { transform: translate(3%, 2%); }
  50%       { transform: translate(-1%, 4%); }
  70%       { transform: translate(2%, -2%); }
  90%       { transform: translate(-3%, 1%); }
`;

const progressAnim = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

/* ── Styled Components ── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
`;

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  height: 90vh;
  min-height: 560px;
  overflow: hidden;
  background: #0a0804;
  font-family: "Jost", sans-serif;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const Slide = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 0 8vw 10vh;
`;

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  transform: scale(1.06);
  transition: transform 8s ease;
  .active & {
    transform: scale(1);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(8, 5, 2, 0.82) 0%,
      rgba(8, 5, 2, 0.3) 55%,
      rgba(8, 5, 2, 0.1) 100%
    );
  }
`;

const GrainOverlay = styled.div`
  position: absolute;
  inset: -50%;
  width: 200%;
  height: 200%;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  opacity: 0.045;
  pointer-events: none;
  animation: ${grain} 6s steps(1) infinite;
  z-index: 10;
`;

const Content = styled.div`
  position: relative;
  z-index: 5;
  max-width: 680px;
`;

const Label = styled(motion.span)`
  display: inline-block;
  font-family: "Jost", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c9a96e;
  margin-bottom: 1rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #c9a96e44;
`;

const Title = styled(motion.h1)`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2.8rem, 6vw, 5.2rem);
  line-height: 1.08;
  color: #f5ede0;
  margin: 0 0 1.4rem;
  letter-spacing: -0.01em;

  em {
    font-style: italic;
    color: #c9a96e;
  }
`;

const Para = styled(motion.p)`
  font-family: "Jost", sans-serif;
  font-weight: 300;
  font-size: clamp(0.9rem, 1.5vw, 1.05rem);
  line-height: 1.8;
  color: rgba(245, 237, 224, 0.72);
  margin: 0 0 2.4rem;
  max-width: 500px;
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: 1px solid #c9a96e;
  color: #c9a96e;
  font-family: "Jost", sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.9rem 2rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: color 0.35s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: #c9a96e;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }

  &:hover {
    color: #0a0804;
    &::before {
      transform: scaleX(1);
    }
  }

  span {
    position: relative;
    z-index: 1;
  }

  svg {
    position: relative;
    z-index: 1;
    transition: transform 0.3s ease;
  }
  &:hover svg {
    transform: translateX(4px);
  }
`;

/* ── Controls ── */
const Controls = styled.div`
  position: absolute;
  bottom: 10vh;
  right: 8vw;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.2rem;
`;

const Dots = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? "2rem" : "0.4rem")};
  height: 0.4rem;
  border-radius: 2px;
  background: ${({ $active }) =>
    $active ? "#c9a96e" : "rgba(245,237,224,0.35)"};
  border: none;
  cursor: pointer;
  transition: all 0.4s ease;
  padding: 0;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const NavBtn = styled.button`
  width: 2.6rem;
  height: 2.6rem;
  border: 1px solid rgba(245, 237, 224, 0.25);
  background: rgba(10, 8, 4, 0.4);
  backdrop-filter: blur(6px);
  color: #f5ede0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.3s,
    background 0.3s;

  &:hover {
    border-color: #c9a96e;
    background: rgba(201, 169, 110, 0.12);
  }
`;

/* ── Progress Bar ── */
const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(245, 237, 224, 0.1);
  z-index: 20;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #c9a96e;
  transform-origin: left;
  animation: ${progressAnim} 3s linear forwards;
`;

/* ── Counter ── */
const Counter = styled.div`
  position: absolute;
  top: 2.5rem;
  right: 8vw;
  z-index: 20;
  font-family: "Cormorant Garamond", serif;
  font-size: 0.85rem;
  color: rgba(245, 237, 224, 0.45);
  letter-spacing: 0.1em;

  span {
    color: #c9a96e;
    font-size: 1.1rem;
  }
`;

/* ── Framer variants ── */
const slideVariants = {
  enter: (dir) => ({
    x: dir > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] },
  },
  exit: (dir) => ({
    x: dir > 0 ? "-100%" : "100%",
    opacity: 0,
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] },
  }),
};

const contentVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.45 + i * 0.1, duration: 0.7, ease: "easeOut" },
  }),
};

/* ── Component ── */
export default function Banner() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [progressKey, setProgressKey] = useState(0);
  const dragStart = useRef(null);
  const INTERVAL = 3000;

  const goTo = (index, dir) => {
    setDirection(dir);
    setCurrent(index);
    setProgressKey((k) => k + 1);
  };

  const next = () => goTo((current + 1) % slides.length, 1);
  const prev = () => goTo((current - 1 + slides.length) % slides.length, -1);

  useEffect(() => {
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [current]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x < -50) next();
    else if (info.offset.x > 50) prev();
  };

  const slide = slides[current];

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        <GrainOverlay />

        <AnimatePresence custom={direction} initial={false}>
          <Slide
            key={slide.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={handleDragEnd}
            className="active"
          >
            <BgImage $src={slide.bg} />
            <Content>
              <Label
                key={`label-${slide.id}`}
                custom={0}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {slide.label}
              </Label>

              <Title
                key={`title-${slide.id}`}
                custom={1}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                dangerouslySetInnerHTML={{ __html: slide.title }}
              />

              <Para
                key={`para-${slide.id}`}
                custom={2}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {slide.paragraph}
              </Para>

              <CTAButton
                key={`cta-${slide.id}`}
                custom={3}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                whileTap={{ scale: 0.97 }}
              >
                <span>{slide.cta}</span>
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                  <path
                    d="M1 5h12M9 1l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CTAButton>
            </Content>
          </Slide>
        </AnimatePresence>

        {/* Counter */}
        <Counter>
          <span>0{current + 1}</span> / 0{slides.length}
        </Counter>

        {/* Bottom controls */}
        <Controls>
          <NavButtons>
            <NavBtn onClick={prev} aria-label="Previous slide">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path
                  d="M13 5H1M5 1L1 5l4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NavBtn>
            <NavBtn onClick={next} aria-label="Next slide">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path
                  d="M1 5h12M9 1l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NavBtn>
          </NavButtons>
          <Dots>
            {slides.map((s, i) => (
              <Dot
                key={s.id}
                $active={i === current}
                onClick={() => goTo(i, i > current ? 1 : -1)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </Dots>
        </Controls>

        {/* Progress bar */}
        <ProgressBar>
          <ProgressFill key={progressKey} />
        </ProgressBar>
      </Wrapper>
    </>
  );
}
